import type { DictionaryWord } from "@-/dev.improve.haniverse.lib.morpheme.word.read";
import { MorphemeInfoPanelContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._context";
import { useWordRowStatsLabels } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._components.word-row._sections.stats";
import {
  Surface,
  SurfaceContent,
  SurfaceFooter,
  SurfaceHeader,
} from "@-/dev.improve.haniverse.components.ui.surface";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { toDiacriticPinyin } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { useMorphemePanelsHrefNavigator } from "@-/dev.improve.haniverse.lib.morpheme.page.navigator";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { cn } from "@-/com.npmjs.cn";
import { type ReactNode, useCallback, useMemo } from "react";
import { ActivityIndicator, Pressable, View } from "@-/com.npmjs.react-native";
import {
  LongPressable,
  useSelectableTextPress,
} from "@-/dev.improve.haniverse.components.ui.long-pressable";
import { useSentenceSpeech } from "@-/dev.improve.haniverse.lib.sentence.speech";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import { morphemeSelectionItem } from "@-/dev.improve.haniverse.routes.nav.app._context";
import { useAppSelection } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.tile.selection";

/**
 * A dictionary word with pinyin below its glyphs and selectable definitions that expand on tap.
 * The header opens the word page; its glyph plays audio. Rank and optional grade sit top right.
 * The body is `children` (example sentences), and `footerCenter` holds their loading/end status.
 */
export function WordRow({
  children,
  className,
  contentClassName,
  footerCenter,
  onNavigate,
  word,
}: {
  children?: ReactNode;
  className?: string;
  contentClassName?: string;
  footerCenter?: ReactNode;
  onNavigate?: () => void;
  word: DictionaryWord;
}) {
  const stats = useWordRowStatsLabels(word);
  const [isDefinitionsExpanded, setIsDefinitionsExpanded] = useRouteBodyState(
    `word-row:${word.word}:definitions-expanded`,
    false,
  );

  const [morphemeHrefObject] = useContextSelectors(
    MorphemeInfoPanelContext,
    (c) => c.morphemeHrefObject,
  );
  const morphemePanelsHrefNavigator = useMorphemePanelsHrefNavigator();
  const selectionItems = useMemo(() => [morphemeSelectionItem(word.word)], [word.word]);
  const { consumePress, isSelected, onLongPress } = useAppSelection(selectionItems);
  const [isMutedOverride] = useContextSelectors(NavContext, (c) => c.isMutedOverride);
  const {
    isPreparing,
    isSpeaking,
    toggle: toggleSound,
  } = useSentenceSpeech(word.word, {
    isMuted: isMutedOverride,
  });

  const handlePress = useCallback(() => {
    if (consumePress()) {
      return;
    }
    onNavigate?.();
    morphemePanelsHrefNavigator.push.mutate({ morpheme: word.word, prefix: morphemeHrefObject });
  }, [consumePress, morphemePanelsHrefNavigator, morphemeHrefObject, onNavigate, word.word]);

  const definitions = word.entry?.definitions ?? [];
  const joinedDefinitions = definitions.join("; ");
  const isDefinitionsExpandable = joinedDefinitions.length > 0;
  // Holding a definition selects its text natively; that hold's release must not toggle it.
  const expandedDefinitionsPress = useSelectableTextPress((event) => {
    event?.stopPropagation();
    setIsDefinitionsExpanded(false);
  });
  const collapsedDefinitionsPress = useSelectableTextPress(
    isDefinitionsExpandable
      ? (event) => {
          event?.stopPropagation();
          setIsDefinitionsExpanded(true);
        }
      : undefined,
  );

  return (
    <Surface className={className}>
      <SurfaceHeader asChild className="px-0">
        <LongPressable
          accessible={false}
          onLongPress={onLongPress}
          onPress={handlePress}
          testID={`word-row-header-${word.id}`}
        >
          <View className="flex-row items-center gap-x-2">
            <LongPressable
              accessibilityLabel={`${isSpeaking || isPreparing ? "Stop" : "Play"} word audio: ${word.word}`}
              accessibilityRole="button"
              accessibilityState={{ busy: isPreparing, selected: isSelected }}
              className={cn(
                "items-center justify-center py-2 pr-1 pl-3 active:bg-accent",
                isSelected && "bg-primary/10",
              )}
              onLongPress={onLongPress}
              onPress={(event) => {
                event?.stopPropagation();
                if (!consumePress()) {
                  toggleSound();
                }
              }}
            >
              <Text
                className={cn(
                  "text-2xl leading-8 font-medium",
                  isSpeaking ? "text-primary" : "text-foreground",
                )}
              >
                {word.word}
              </Text>
              <Text className="text-xs leading-4 text-muted-foreground">
                {word.entry === null ? " " : toDiacriticPinyin(word.entry.pinyin)}
              </Text>
              {isPreparing ? (
                <View className="absolute top-0 right-0">
                  <ActivityIndicator size="small" />
                </View>
              ) : null}
            </LongPressable>
            <View className="min-w-0 flex-1 py-2">
              <View className="min-w-0 flex-1">
                {word.entry === null ? (
                  // The dictionary has no entry for this word (no reading, no definitions) — say so
                  // rather than leave the definitions slot blank.
                  <Text className="text-sm text-muted-foreground italic" testID="word-row-no-entry">
                    No dictionary entry
                  </Text>
                ) : isDefinitionsExpanded ? (
                  <Text
                    accessibilityLabel={`Show fewer definitions for ${word.word}`}
                    accessibilityRole="button"
                    accessibilityState={{ expanded: true }}
                    className="text-sm leading-5 text-muted-foreground"
                    {...expandedDefinitionsPress}
                    selectable
                    testID="word-row-definitions-expanded"
                  >
                    {definitions.map((definition) => `• ${definition}`).join("\n")}
                  </Text>
                ) : (
                  <Text
                    accessibilityLabel={
                      isDefinitionsExpandable ? `Show all definitions for ${word.word}` : undefined
                    }
                    accessibilityRole={isDefinitionsExpandable ? "button" : undefined}
                    accessibilityState={isDefinitionsExpandable ? { expanded: false } : undefined}
                    className="text-sm leading-5 text-muted-foreground"
                    numberOfLines={2}
                    {...collapsedDefinitionsPress}
                    selectable
                  >
                    {joinedDefinitions}
                  </Text>
                )}
              </View>
            </View>
            <View className="items-end self-start py-2 pr-3">
              <Pressable
                accessibilityLabel={`Open word page for ${word.word}`}
                accessibilityRole="link"
                onPress={(event) => {
                  event?.stopPropagation();
                  handlePress();
                }}
              >
                <Text className="text-right text-xs text-muted-foreground">
                  {stats.rankLabel ?? "No Rank"}
                </Text>
                {stats.levelLabel ? (
                  <Text className="text-right text-xs text-muted-foreground">
                    {stats.levelLabel}
                  </Text>
                ) : null}
              </Pressable>
            </View>
          </View>
        </LongPressable>
      </SurfaceHeader>
      {children === undefined ? null : (
        <SurfaceContent className={contentClassName}>{children}</SurfaceContent>
      )}
      {footerCenter ? (
        <SurfaceFooter className="justify-center py-1.5">{footerCenter}</SurfaceFooter>
      ) : null}
    </Surface>
  );
}
